/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { setAuthTokenProvider } from '@/lib/apiClient';
import { setAccessTokenClaims, setTokenClaimsProvider } from '@/lib/tokenClaims';
import { authMode, tokenProvider } from './msalConfig';
import { mirrorAccessTokenToCookie } from './cookieMirror';

/**
 * Wires auth into the API layer at boot, before the first render: in msal mode it bridges the MSAL
 * access token (and the /api cookie the Unity WebGL assets need) into axios; in mock mode it seeds
 * role/partner claims from Vite env vars.
 *
 * Runs at startup, not in a React effect: effects fire child-first, so a provider-level effect
 * would register only after the boot queries already fired, leaving the earliest requests
 * unauthenticated (gateway 404s that clear on refresh).
 */
export function registerAuthBridge(): void {
  if (authMode !== 'msal') {
    // Dev-only: let a mock partner type / roles drive dealer-vs-internal UX without a real IdP.
    const partnerType = import.meta.env.VITE_MOCK_PARTNER_TYPE;
    const roles = import.meta.env.VITE_MOCK_ROLES;
    setTokenClaimsProvider(() => ({
      ...(partnerType ? { gmVSSMPartnerType: partnerType } : {}),
      ...(roles
        ? {
            roles: roles
              .split(',')
              .map((r) => r.trim())
              .filter(Boolean),
          }
        : {}),
    }));
    return;
  }
  setAuthTokenProvider(async () => {
    const token = await tokenProvider();
    mirrorAccessTokenToCookie(token);
    // Keep dealer/role detection in sync with the freshest token (e.g. after a silent refresh).
    setAccessTokenClaims(token);
    return token;
  });
}
